import React, { useState, useEffect, useCallback, useMemo } from "react";
import styled from "styled-components";
import { withRouter, Redirect } from "react-router-dom";
import queryString from "query-string";
import { withLDConsumer } from "launchdarkly-react-client-sdk";

import logger from "../../utils/LoggerFrontend";
import Actions from "../../Actions";
import CampaignCreatePreviews from "../../PagesUser/SelfServiceCampaignCreation/Previews/campaign-create-previews/CampaignCreatePreviews";
import { InfoBanner } from "../../PagesUser/SelfServiceCampaignCreation/Previews/Components";
import {
  DEFAULT_MINUTES_LEFT_TO_START,
  EDITABLE_STATUS
} from "../../PagesUser/SelfServiceCampaignCreation/Previews/constants";
import {
  getSecondsLeft,
  convertPostsToAdPreviews,
  preparePostsForSaving,
  getRedirectUrl
} from "../../PagesUser/SelfServiceCampaignCreation/Previews/utils";
import CampaignCreateSupportPanel from "../../PagesUser/SelfServiceCampaignCreation/Previews/Campaign__Create_SupportPanel";

import { URLEditor, AdminTopActionBar, StatusWindow, ClientPanel } from "./Components";
import { withRefluxStore, PreviewStore, MediaLibraryStore } from "../../Stores";
import useDisableContextMenu from "../../hooks/useDisableContextMenu";
import { CampaignContextProvider } from "../../context/Campaign.context.jsx";

const CampaignCreatePreviewsWrapper = styled.div`
  margin-top: 20px;
  text-align: center;
`;

const AdPreviewToolSelfService = ({
  match: {
    params: { campaignId, adsetId }
  },
  history,
  clientView = false,
  previewOnly = false,
  isExternal = false,
  posts,
  released,
  loading,
  channelsData,
  campaignCreationLoadingState,
  userProfile,
  campaignData,
  adsetData,
  location,
  adsets = [],
  campaignManagers,
  organization,
  updatingDestinationLink
}) => {
  logger.log("AdPreviewToolSelfService:render", "AdPreviewToolSelfService prepared", {
    history,
    clientView,
    previewOnly,
    isExternal,
    posts,
    released,
    loading,
    channelsData,
    campaignCreationLoadingState,
    userProfile,
    campaignData,
    adsetData,
    location,
    adsets,
    campaignManagers,
    organization,
    updatingDestinationLink
  });

  const [isReleased, setIsReleased] = useState(clientView);
  const [startingDate, setStartingDate] = useState();
  const [showStartingDatePanel, setShowStartingDatePanel] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState();
  const [status, setStatus] = useState("booking");
  const [hasAts, setHasAts] = useState(false);

  const { organizationId: orgaId, deployed } = campaignData;

  const userIsAdmin = useMemo(
    () => userProfile && userProfile.superRank === "admin",
    [userProfile]
  );

  // Disable context menu to avoid the clients download the images
  useDisableContextMenu(userProfile);

  const values = useMemo(() => queryString.parse(location?.search), [location]);

  // Update the starting date of the campaign
  const changeStartingDate = useCallback(
    (newStartingDate) => {
      setStartingDate(newStartingDate);
      Actions.updateCampaignWithId(campaignId, {
        startDate: newStartingDate.toISOString()
      });
    },
    [campaignId]
  );

  // Updates ad_previews collection on the related campaigns record
  const saveOldPreview = useCallback(() => {
    const mappedPosts = preparePostsForSaving({ posts, channelsData, campaignId });
    logger.log("AdPreviewToolSelfService:saveOldPreview", "Save old preview", {
      mappedPosts,
      campaignId,
      adsetId
    });

    const oldPreviews = convertPostsToAdPreviews(mappedPosts, campaignData);
    return oldPreviews;
  }, [posts, channelsData, campaignId, campaignData]);

  // Delete campaign startDate, set status to WAITING_FOR_APPROVAL
  const setCampaignWaitingForApproval = useCallback(() => {
    logger.log("setCampaignWaitingForApproval", "called", {
      campaignId,
      displayName: userProfile.userName
    });
    setShowStartingDatePanel(false);
    Actions.updateCampaignStatus(campaignId, "WAITING_FOR_APPROVAL", userProfile.userName, false);
    Actions.updateCampaignWithId(campaignId, {
      originalStartDate: campaignData.startDate || null
    });
    Actions.deleteCampaignField(campaignId, "startDate");
    setStartingDate();
    setStatus("WAITING_FOR_APPROVAL");
    saveOldPreview();
  }, [campaignId, userProfile, saveOldPreview, campaignData.startDate]);

  // Listener for the campaign loading, loads the campaing to the preview store, gets images and teaser texts, get starting date
  useEffect(() => {
    const initializeLocalStatesAndFetchEditorData = ({ campaignData }) => {
      const {
        startDate,
        createdDate,
        status,
        activeSourcingFeature,
        companyName,
        jobPostingUrl,
        jobTitle,
        organizationId,
        channels,
        locations,
        jobCategoryId,
        language,
        orgaName,
        companyId
      } = campaignData;

      // Starting date
      const initialStartingDate = startDate
        ? new Date(startDate)
        : new Date(new Date(createdDate).getTime() + DEFAULT_MINUTES_LEFT_TO_START * 60000);
      setStartingDate(initialStartingDate);
      const currentDateObj = new Date();
      const secondsLeft =
        initialStartingDate.getTime() < currentDateObj.getTime() || status != "SCHEDULED"
          ? 0
          : getSecondsLeft(initialStartingDate, currentDateObj);
      setSecondsLeft(secondsLeft);
      setShowStartingDatePanel(!!secondsLeft);

      // Status
      setStatus(status);

      // Check whether campaign is BewerberHub campaign
      setHasAts(!!activeSourcingFeature);

      // Fetch editor data (images, teaser text, facebook pages)
      Actions.getHistoricImages({
        companyName,
        orgaId: organizationId,
        orgaName,
        companyId
      });

      logger.log("AdPreviewToolSelfService:getImagesFromWebsite", "getImagesFromWebsite", {
        jobPostingUrl,
        orgaName,
        companyName,
        organizationId
      });

      Actions.getImagesFromWebsite(jobPostingUrl, orgaName, companyName, organizationId);

      Actions.getTeaserTextRecommendation({
        url: jobPostingUrl,
        jobTitle: jobTitle,
        companyName: companyName,
        location: locations?.[0],
        orgaId: organizationId,
        language: language || "de"
      });

      Actions.loadOrganizationOfCampaign(organizationId);

      if (channels.includes("facebook") || channels.includes("instagram")) {
        Actions.getAllFacebookPages();
      }
      if (adsetId) {
        Actions.loadAdsetDataPublic(adsetId);
      }

      Actions.getJobCategoryWithId(jobCategoryId);
    };

    const unsubscribeGetCampaignAndAdCreativesByCampaignId =
      Actions.getCampaignAndAdCreativesByCampaignId.completed.listen(
        initializeLocalStatesAndFetchEditorData
      );

    return () => {
      unsubscribeGetCampaignAndAdCreativesByCampaignId();
      Actions.resetPreviewData();
    };
  }, [userIsAdmin, adsetId, campaignId, clientView]);

  // Loads the campaign and the posts to the stores by initialising actions when campaignId changes
  useEffect(() => {
    // If the adset id is not passed in, fetch the adsets for this campaign and redirect
    if (!adsetId) {
      logger.log(
        "AdPreviewToolSelfService:componentDidMount",
        "has no adset, getAdsetsByCampaignIdPublic",
        {
          campaignId,
          adsetId,
          clientView
        }
      );
      Actions.getAdsetsByCampaignIdPublic(campaignId);
    }

    // If we do have adsets then continue fetching ad creatives
    else {
      logger.log(
        "AdPreviewToolSelfService:componentDidMount",
        "has adset. getCampaignAndAdCreativesByCampaignId",
        {
          campaignId,
          adsetId,
          clientView
        }
      );
    }
  }, [campaignId, adsetId, clientView]);

  useEffect(() => {
    const ref = Actions.loadSingleCampaign.completed.listen((campaign) => {
      logger.log("AdPreviewTool", "loadSingleCampaign campaign loaded", campaign);
      ref();

      const { organizationId } = campaign;
      if (organizationId) {
        Actions.getCampaignAndAdCreativesByCampaignId(campaignId, clientView, organizationId);
      }
    });

    const refFailed = Actions.loadSingleCampaign.failed.listen((error) => {
      logger.error("AdPreviewTool", "loadSingleCampaign unable to load campaign", { error });
      refFailed();
    });

    Actions.loadSingleCampaign(campaignId);
  }, [campaignId, clientView]);

  // Redirects the user to the client view if the user is not admin
  useEffect(() => {
    if (userProfile && userProfile.superRank !== "admin" && !isExternal) {
      logger.log("AdPreviewToolSelfService", "redirecting non-admin user", {
        path: history.location.pathname,
        campaignId
      });
      history.replace(`/creatives/${campaignId}`);
    }
  }, [campaignId, history, userProfile, isExternal]);

  const campaignStarted = useMemo(
    () => startingDate && secondsLeft === 0,
    [startingDate, secondsLeft]
  );

  const editable = useMemo(() => {
    if (previewOnly) {
      return false;
    }

    if (EDITABLE_STATUS.includes(status)) {
      return true;
    }

    if (!clientView) {
      return true;
    }

    if (!loading && !campaignStarted) {
      return true;
    }

    return false;
  }, [loading, clientView, campaignStarted, status, previewOnly]);

  const isUrlEditorEditable = useMemo(() => {
    return !isExternal && (!hasAts || !clientView);
  }, [isExternal, hasAts, clientView]);

  // Show link editor on the bottom of each channel if it's admin or agency
  const showLinkEditor = useMemo(
    () => !clientView || !hasAts || !previewOnly,
    [clientView, hasAts, previewOnly]
  );

  // Redirect to login page if there is no user logged in and we are not on an external
  // ad preview (eg Onlyfy) or if we are showing the adpreview only
  if (!userProfile && !previewOnly && !isExternal) {
    const redirectUrl = getRedirectUrl();
    logger.debug("AdPreviewToolSelfService:render", "called...", { redirectUrl, campaignId });
    return <Redirect to={"/login?url=/" + redirectUrl} />;
  }

  // If we don't have an adset on the url then add the fetched adset on the url
  if (!adsetId && adsets?.length) {
    const adsetId = adsets?.length ? adsets[0]?.id : "";
    logger.log("AdPreviewToolSelfService:render", "adset id not on url. Redirecting...", {
      adsetId,
      adsets,
      campaignId,
      isExternal,
      path: history?.location?.pathname
    });
    return <Redirect push to={(history?.location?.pathname + `/${adsetId}`).replace("//", "/")} />;
  }

  const orgaLinkedInPageID = organization?.linkedInPageId ?? null;

  return (
    <CampaignContextProvider>
      <CampaignCreatePreviewsWrapper>
        {userIsAdmin && !previewOnly && (
          <>
            {!loading && !deployed && !clientView && (
              <InfoBanner status="stagingWarning" top="62px" />
            )}
            <AdminTopActionBar
              orgaId={orgaId}
              campaignId={campaignId}
              clientView={clientView}
              adsetId={adsetId}
            />
          </>
        )}
        <CampaignCreatePreviews
          isExternal={isExternal}
          isReleased={isReleased}
          editable={editable}
          secondsLeft={secondsLeft}
          showStartingDatePanel={showStartingDatePanel}
          setSecondsLeft={setSecondsLeft}
          startingDate={startingDate}
          changeStartingDate={changeStartingDate}
          setCampaignWaitingForApproval={setCampaignWaitingForApproval}
          orgaId={orgaId}
          tbCampaignId={campaignId}
          tbAdsetId={adsetId}
          clientView={clientView}
          hasAts={hasAts}
          showLinkEditor={showLinkEditor}
          preselectedChannelPreview={values?.channel}
          orgaLinkedInPageID={orgaLinkedInPageID}
        >
          {!loading && !previewOnly && (
            <>
              <ClientPanel
                status={status}
                setStatus={setStatus}
                campaignId={campaignId}
                orgaId={campaignData.organizationId}
                userName={userProfile?.userName}
                userProfile={userProfile || {}}
                orgaName={campaignData.orgaName}
                campaignManager={campaignData.campaignManager}
                setShowStartingDatePanel={setShowStartingDatePanel}
                startDate={campaignData.startDate}
                source={campaignData.source}
                jobUpdatesUrl={campaignData.jobUpdatesUrl}
                duration={adsetData?.duration || campaignData?.campaignDuration}
                adsetId={adsetId}
                organization={organization}
              />
              <StatusWindow status={status} startDate={campaignData.startDate} />
            </>
          )}
          <URLEditor
            isClientView={clientView}
            hasAts={hasAts}
            campaignData={campaignData}
            editable={isUrlEditorEditable}
            isReleased={isReleased}
            channelsData={channelsData}
            released={released}
            userName={userProfile?.userName}
            campaignId={campaignId}
            loading={updatingDestinationLink}
          />
          {
            <CampaignCreateSupportPanel
              userProfile={userProfile || {}}
              setStatus={setStatus}
              setShowStartingDatePanel={setShowStartingDatePanel}
              editable={editable}
              clientView={clientView}
              campaignData={campaignData}
              campaignManagers={campaignManagers}
            />
          }
        </CampaignCreatePreviews>
      </CampaignCreatePreviewsWrapper>
    </CampaignContextProvider>
  );
};

// Wrap the ad preview tool to allow for caller to inject custom user profiles.
// Used for external campaigns with anonymous users.
const AdPreviewToolSelfServiceWrapper = ({ injectedUserProfile, ...props }) =>
  AdPreviewToolSelfService({ ...props, userProfile: injectedUserProfile || props.userProfile });

export default withRefluxStore(
  withRouter(withLDConsumer()(AdPreviewToolSelfServiceWrapper)),
  [PreviewStore, MediaLibraryStore],
  [
    "posts",
    "released",
    "loading",
    "campaignData",
    "adsetData",
    "adsets",
    "userProfile",
    "channelsData",
    "campaignCreationLoadingState",
    "campaignManagers",
    "organization",
    "updatingDestinationLink"
  ]
);
